import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import { destinationData } from './destinationData';
import DestinationInquiryForm from './components/DestinationInquiryForm';

export default function DestinationTemplate() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const destination = destinationData[slug || ''];

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleBackClick = () => {
    navigate('/destinations');
  };

  if (!destination) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <h1 className="font-serif text-4xl text-black mb-4">Destination Not Found</h1>
            <Link to="/destinations" className="text-black underline">
              Return to Destinations
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Back Button - Fixed Position */}
      <button
        onClick={handleBackClick}
        className="fixed top-24 left-8 z-40 flex items-center gap-3 px-5 py-3 bg-white/95 backdrop-blur-sm text-black text-sm tracking-wider hover:bg-black hover:text-white transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
      >
        <i className="ri-arrow-left-line text-lg"></i>
        BACK TO DESTINATIONS
      </button>

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src={destination.heroImage}
            alt={destination.name}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        </div>
        
        <div className="relative z-10 h-full flex items-center justify-center px-8">
          <div className="text-center max-w-4xl">
            <div className="w-20 h-px bg-white mx-auto mb-8"></div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white mb-6">
              {destination.name}
            </h1>
            <p className="text-xl md:text-2xl text-white/90">
              {destination.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="w-20 h-px bg-black mb-8"></div>
          <h2 className="font-serif text-4xl md:text-5xl text-black mb-12">
            Overview
          </h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            {destination.overview.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Supporting Images Gallery */}
      {destination.images && destination.images.length > 0 && (
        <section className="py-16 px-8 lg:px-16 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <div className="w-20 h-px bg-black mb-8"></div>
            <h2 className="font-serif text-3xl md:text-4xl text-black mb-12">
              Discover {destination.name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {destination.images.map((image, index) => (
                <div key={index} className="relative w-full h-[320px] overflow-hidden rounded-lg shadow-lg">
                  <img
                    src={image}
                    alt={`${destination.name} - View ${index + 1}`}
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Charter Here */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <div className="w-20 h-px bg-black mb-8"></div>
          <h2 className="font-serif text-4xl md:text-5xl text-black mb-12">
            Why Charter Here
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-black text-white rounded-full mb-6">
                <i className="ri-calendar-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">Best Time to Charter</h3>
              <p className="text-gray-700 leading-relaxed">{destination.bestTime}</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-black text-white rounded-full mb-6">
                <i className="ri-route-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">Typical Itineraries</h3>
              <p className="text-gray-700 leading-relaxed">{destination.itineraries}</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-black text-white rounded-full mb-6">
                <i className="ri-ship-line text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-black mb-4">Ideal Yacht Size</h3>
              <p className="text-gray-700 leading-relaxed">{destination.yachtSize}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 lg:px-16 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-px bg-black mx-auto mb-8"></div>
          <h2 className="font-serif text-4xl md:text-5xl text-black mb-8">
            Ready to Explore {destination.name}?
          </h2>
          <p className="text-lg text-gray-700 mb-12 leading-relaxed">
            Discover available yachts for charter in this destination and start planning your perfect voyage.
          </p>
          <Link
            to="/yacht-charter"
            className="inline-block px-12 py-5 bg-black text-white text-sm tracking-widest hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            VIEW YACHTS AVAILABLE IN THIS DESTINATION
          </Link>
        </div>
      </section>

      {/* Destination Inquiry Form */}
      <DestinationInquiryForm destinationName={destination.name} />

      {/* Legal Disclaimer */}
      <section className="py-12 px-8 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-gray-500 text-center leading-relaxed">
            Destination information is provided for general guidance only. Yacht availability, itineraries, and charter terms vary by vessel and season.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
